import { array, object, string } from "yup";
import { useEffect, useState } from "react";

import Validations from "@helpers/validations";
import { decoColumns } from "./deco-columns";
import size from "lodash/size";
import useAxios from "@hooks/use-axios";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

const defaultValues = {
    name      : "",
    conditions: [{ condition: "attribute", name: "", operator: "", value: "" }],
    action    : "add",
    _id       : "",
    lifecycle : ""
};
const useGoals = props => {
    const {
        modalConfig: { row },
        isEdit,
        handleGoals,
        goals
    } = props;
    const { platformId } = useParams();   
    const { requiredValidation } = Validations();
    const { axiosInstance, loading, cancelAllRequests } = useAxios({
        base: "panel"
    });
    const { axiosInstance: upsertAxiosInstance, loading: updateLoading } =
        useAxios({
            base: "panel"
        });
    const [isOpen, setIsOpen] = useState(false);

    const handleModal = () => setIsOpen(prev => !prev);
    const validationSchema = object().shape({
        name      : requiredValidation({ name: "Goal Name" }),
        conditions: array()
            .of(
                object().shape({
                    condition: requiredValidation({ name: "Condition" }),

                    name: string().when("condition", ([condition], schema) => {
                        return condition === "manually_start" ? schema : requiredValidation({ name: "Name" });
                    }),

                    operator: string().when(
                        "condition",
                        ([condition], schema) => {
                            return condition === "attribute" ? requiredValidation({ name: "Operator" }) : schema;
                        }
                    ),

                    value: string().when("condition", ([condition], schema) => {
                        return condition === "manually_start" ? schema : requiredValidation({ name: "Value" });
                    })
                })
            )
            .min(1, "At least one start condition is required"),
        action   : string().optional(),
        _id      : string().optional(),
        lifecycle: string().optional()
    });

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(validationSchema)
    });
    const { isDirty } = methods.formState;

    const getGoals = async () => {
        const { data } = await axiosInstance.get(`/goal/${platformId}/${row._id}`);
        handleGoals(data);
    };

    const upsertGoal = async () => {
        methods.handleSubmit(async data => {
            if (isEdit) {
                const payload = {
                    name      : data.name,
                    conditions: data.conditions.map(item => ({
                        condition: item.condition,
                        name     : item.name,
                        operator : item.operator,
                        value    : item.operator ? Number(item.value) : item.value
                    })),
                    ...(data.action === "edit" ? { lifecycle_id: data.lifecycle } : {
                        lifecycle_id: row._id
                    })
                };
                if (data.action === "edit") {
                    await upsertAxiosInstance.patch(
                        `/goal/${platformId}/${data._id}`,
                        payload
                    );
                } else {
                    await upsertAxiosInstance.post(`/goal/${platformId}`, payload);
                }
                getGoals();
                handleModal();
            } else {
                const formattedGoal = {
                    _id       : data._id || `temp-${Date.now()}`, // temp id if not present
                    name      : data.name,
                    conditions: data.conditions.map(item => ({
                        condition: item.condition,
                        name     : item.name,
                        operator : item.operator,
                        value    : item.operator ? Number(item.value) : item.value
                    }))
                };

                let updatedGoals = [...goals];

                if (data.action === "add") {
                    updatedGoals.push(formattedGoal);
                } else if (data.action === "edit") {
                    updatedGoals = updatedGoals.map(goal => (goal._id === data._id ? formattedGoal : goal)
                    );
                } else if (data.action === "delete") {
                    updatedGoals = updatedGoals.filter(
                        goal => goal._id !== data._id
                    );
                }

                handleGoals(updatedGoals);
                handleModal();
            }
        })();
    };

    useEffect(() => {
        let isActive = true;
        if (isActive && isEdit) {
            getGoals();
        }
        return () => {
            isActive = false;
            cancelAllRequests();
        };
    }, []);

    const handleRowActions = async (row, action) => {
        if (action !== "delete") {
            if (size(row)) {
                methods.reset({ ...row, action });
            } else {
                methods.reset({ ...defaultValues, action });
            }
            handleModal();
        } else {
            if (isEdit) {
                await upsertAxiosInstance.delete(`/goal/${platformId}/${row._id}`);
                getGoals();
            } else {
                handleGoals(goals.filter(
                    goal => goal._id !== row._id
                ));
            }
        }
    };

    return {
        loading,
        columns : decoColumns({ handleRowActions }),
        methods,
        isDirty,
        updateLoading,
        handleModal,
        isEdit,
        onSubmit: upsertGoal,
        isOpen,
        handleRowActions
    };
};
export default useGoals;
